import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { usePixelatedConfig } from "../config/config.client";
import { SmartImage } from "../elements/smartimage";
import "../../css/pixelated.grid.scss";
import "./resume.css";
/*
Resume Microformat - https://microformats.org/wiki/h-resume
Details Summary Expand Collapse - https://www.w3schools.com/tags/tag_details.asp
*/
function isValidDate(dateString) {
    let date = new Date(dateString);
    return !isNaN(date.getTime());
}
/* function isStr(str) {
    const myStr = String(str).trim();
    return myStr != null && myStr !== "" && myStr.length > 0 && typeof myStr !== 'undefined';
} */
// type IObj = { [key: string]: any }
const defaultDateFormat = 'MM/yyyy';
/* type resumeProps = {
    title: string
    data: any
    dateFormat?: string
    collapsible?: boolean
}

type simpleResumeProps = {
    data: any
} */
/**
 * Resume — Render a microformatted resume (h-resume) object into sections like contact, education, experience, and skills.
 *
 * @param {any} [props.data] - Resume data object (h-resume structured data expected).
 */
Resume.propTypes = {
    /** Resume data object (h-resume) */
    data: PropTypes.any.isRequired,
};
export function Resume(props) {
    return (_jsx("section", { className: "p-resume", id: "resume-section", children: _jsx("div", { className: "section-container", children: _jsxs("div", { className: "row-12col", children: [_jsx("div", { className: "p-name grid-s1-e13", children: _jsx(ResumeName, { data: props.data?.items?.[0]?.properties?.name || '' }) }), _jsxs("div", { className: "divider grid-s1-e4", children: [_jsx("div", { className: "p-contact", children: _jsx(ResumeContact, { title: "Contact Information", data: props.data?.items?.[0]?.properties?.contact || [] }) }), _jsx("div", { className: "p-education", children: _jsx(ResumeEvents, { title: "Education", data: props.data?.items?.[0]?.properties?.education || [], dateFormat: "MM/yyyy", collapsible: false }) }), _jsx("div", { className: "p-skills", children: _jsx(ResumeSkills, { title: "Skills", data: props.data?.items?.[0]?.properties?.skills || [] }) })] }), _jsxs("div", { className: "grid-s4-e13", children: [_jsx("div", { className: "p-summary", children: _jsx(ResumeSummary, { title: "Professional Summary", data: props.data?.items?.[0]?.properties?.summary || '' }) }), _jsx("div", { className: "p-qualifications", children: _jsx(ResumeQualifications, { title: "Professional Qualifications", data: props.data?.items?.[0]?.properties?.qualifications || [] }) }), _jsx("div", { className: "p-experience", children: _jsx(ResumeEvents, { title: "Work History", data: props.data?.items?.[0]?.properties?.experience || [], dateFormat: "MM/yyyy", collapsible: false }) }), _jsx("div", { className: "p-projects", children: _jsx(ResumeProjects, { title: "Projects", data: props.data?.items?.[0]?.properties?.experience || [], collapsible: true }) }), _jsx("div", { className: "p-volunteer", children: _jsx(ResumeEvents, { title: "Volunteer Work", data: props.data?.items?.[0]?.properties?.volunteer || [], dateFormat: "MM/yyyy", collapsible: true }) }), _jsx("div", { className: "p-certifications", children: _jsx(ResumeEvents, { title: "Certifications", data: props.data?.items?.[0]?.properties?.certifications || [], dateFormat: "MM/yyyy", collapsible: true }) }), _jsx("div", { className: "p-awards", children: _jsx(ResumeEvents, { title: "Honors & Awards", data: props.data?.items?.[0]?.properties?.awards || [], dateFormat: "MM/yyyy", collapsible: true }) }), _jsx("div", { className: "p-training", children: _jsx(ResumeEvents, { title: "Training & Conferences", data: props.data?.items?.[0]?.properties?.training || [], dateFormat: "MM/dd/yyyy", collapsible: true }) }), _jsx("div", { className: "p-references", children: _jsx(ResumeReferences, { title: "References", data: props.data?.items?.[0]?.properties?.references || [], collapsible: true }) })] })] }) }) }));
}
/**
 * ResumeName — Render the candidate's name from the resume data.
 *
 * @param {any} [props.data] - Array containing name values; the first entry is used as the display name.
 */
ResumeName.propTypes = {
    /** Array containing the name value(s) */
    data: PropTypes.any.isRequired,
};
export function ResumeName(props) {
    const myName = props.data[0];
    return (_jsx(_Fragment, { children: _jsx("h1", { className: "p-name", children: myName }) }));
}
/**
 * ResumeContact — Renders contact information (email, address, phone, website) from resume data.
 *
 * @param {any} [props.data] - Contact microformat data array (expects typical contact properties).
 * @param {string} [props.title] - Section heading to display above contact information.
 */
ResumeContact.propTypes = {
    /** Contact microformat data array */
    data: PropTypes.any.isRequired,
    /** Section title */
    title: PropTypes.string.isRequired,
};
export function ResumeContact(props) {
    const myContact = props.data[0];
    return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), _jsxs("ul", { children: [_jsx("li", { children: _jsx("span", { className: "p-email", children: _jsx("a", { href: `mailto:${myContact?.properties.email[0]}`, target: "_blank", rel: "noopener noreferrer", children: myContact?.properties.email[0] }) }) }), _jsxs("li", { children: [_jsxs("span", { className: "p-street-address", children: [myContact?.properties.adr[0].properties["street-address"], ", "] }), _jsxs("span", { className: "p-locality", children: [myContact?.properties.adr[0].properties.locality, ", "] }), _jsxs("span", { className: "p-region", children: [myContact?.properties.adr[0].properties.region, " "] }), _jsx("span", { className: "p-postal-code", children: myContact?.properties.adr[0].properties["postal-code"] })] }), _jsx("li", { children: _jsx("span", { className: "p-tel", children: myContact?.properties.tel[0] }) }), _jsx("li", { children: _jsx("span", { className: "p-url", children: _jsx("a", { href: myContact?.properties.url[0], target: "_blank", rel: "noopener", children: myContact?.properties.url[0] }) }) })] })] }));
}
/**
 * ResumeEvents — Render a list of dated events (education, work, volunteer) with optional collapsible behavior.
 *
 * @param {any} [props.data] - Array of event objects with `properties` including start/end and location metadata.
 * @param {string} [props.dateFormat] - Date format string passed to `date-fns` for rendering dates (e.g., 'MM/yyyy').
 * @param {boolean} [props.collapsible] - If true, the event list is rendered inside a collapsible `<details>` element.
 * @param {string} [props.title] - Section title for the events list.
 */
ResumeEvents.propTypes = {
    /** Array of event objects (work, education, etc.) */
    data: PropTypes.any.isRequired,
    /** Date format string for display */
    dateFormat: PropTypes.string.isRequired,
    /** When true, events are shown inside a collapsible details element */
    collapsible: PropTypes.bool,
    /** Section title */
    title: PropTypes.string.isRequired,
};
export function ResumeEvents(props) {
    const config = usePixelatedConfig();
    const myElems = [];
    const myEvents = props.data;
    // SORT EVENTS DESCENDING BY END DATE
    myEvents.sort((a, b) => {
        if (a.properties.end[0] < b.properties.end[0]) {
            return 1;
        }
        if (a.properties.end[0] > b.properties.end[0]) {
            return -1;
        }
        return 0;
    });
    for (const iKey in myEvents) {
        // PRE-LOAD SOME VALUES
        const myEvent = myEvents[iKey].properties;
        const myStartDate = isValidDate(myEvent.start[0]) ? format(new Date(myEvent.start[0]), props.dateFormat || defaultDateFormat) : myEvent.start[0];
        const myEndDate = isValidDate(myEvent.end[0]) ? format(new Date(myEvent.end[0]), props.dateFormat || defaultDateFormat) : myEvent.end[0];
        const myLocation = myEvent.location[0].properties;
        const myElemDt = _jsxs("span", { children: [(myStartDate) ? _jsx("span", { className: "dt-start", children: myStartDate }) : null, (myStartDate && myEndDate) ? _jsx("span", { children: " - " }) : null, (myEndDate) ? _jsx("span", { className: "dt-end", children: myEndDate }) : null, _jsx("span", { children: " : " })] });
        // CREATE THE NEW ELEMENT
        const myElem = _jsxs("li", { children: [(props.dateFormat) ? myElemDt : null, (myLocation["job-title"]) ? (_jsxs("span", { className: "p-job-title", children: [myLocation["job-title"], ", "] })) : null, _jsx("span", { className: "p-org", children: myLocation.org }), (myLocation.locality[0]) ? _jsxs("span", { className: "p-locality", children: [", ", myLocation.locality[0]] }) : null, (myLocation.region[0]) ? _jsxs("span", { className: "p-region", children: [", ", myLocation.region[0], " "] }) : null, (myLocation.url[0])
                    ? _jsx("a", { href: myLocation.url[0], target: "_blank", rel: "noreferrer", children: _jsx(SmartImage, { src: "https://www.pixelated.tech/images/icons/link.png", title: myLocation.url[0], alt: myLocation.url[0], className: 'u-url-icon', cloudinaryEnv: config?.integrations?.cloudinary?.product_env, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms }) }) : null] }, iKey);
        // ADD TO THE ARRAY
        myElems.push(myElem);
    }
    if (props.collapsible && props.collapsible == true) {
        return (_jsx(_Fragment, { children: _jsxs("details", { children: [_jsx("summary", { children: _jsx("h2", { children: props.title }) }), _jsx("ul", { children: myElems })] }) }));
    }
    else {
        return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), _jsx("ul", { children: myElems })] }));
    }
}
/**
 * ResumeQualifications — Render grouped qualification lists (e.g., certifications, licenses) by category.
 *
 * @param {any} [props.data] - Object mapping qualification categories to arrays of items.
 * @param {string} [props.title] - Section title to display above qualifications.
 */
ResumeQualifications.propTypes = {
    /** Qualifications data by category */
    data: PropTypes.any.isRequired,
    /** Section title */
    title: PropTypes.string.isRequired,
};
export function ResumeQualifications(props) {
    const myElems = [];
    const myQual = props.data;
    for (const iKey in myQual) {
        const qual = myQual[iKey];
        const myElem = _jsx("h3", { children: iKey }, iKey);
        const quals = qual.map((qualItem, iKey) => _jsx("li", { className: "p-qualification", children: qualItem }, "i" + iKey));
        myElems.push(myElem);
        myElems.push(_jsx("ul", { children: quals }, "q-" + iKey));
    }
    return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), myElems] }));
}
/**
 * ResumeSkills — Render skill categories and values from the resume data.
 *
 * @param {any} [props.data] - Skills data (typically an object or array of categories and skill lists).
 * @param {string} [props.title] - Section title for the skills block.
 */
ResumeSkills.propTypes = {
    /** Skills data (categories and lists) */
    data: PropTypes.any.isRequired,
    /** Section title */
    title: PropTypes.string.isRequired,
};
export function ResumeSkills(props) {
    const myElems = [];
    const mySkills = props.data[0];
    for (const skill in mySkills) {
        const myElem = _jsxs("h3", { className: "p-skill-category", children: [skill, " : "] }, "c-" + skill);
        myElems.push(myElem);
        const myElem2 = _jsxs("span", { className: "p-skill", children: [mySkills[skill], _jsx("br", {})] }, "s-" + skill);
        myElems.push(myElem2);
    }
    return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), _jsx("div", { className: "p-skills", children: myElems })] }));
}
/**
 * ResumeSummary — Render a short professional summary paragraph or bullets.
 *
 * @param {any} [props.data] - Summary text or array of summary paragraphs.
 * @param {string} [props.title] - Section title for the summary block.
 */
ResumeSummary.propTypes = {
    /** Summary text or paragraphs */
    data: PropTypes.any.isRequired,
    /** Section title */
    title: PropTypes.string.isRequired,
};
export function ResumeSummary(props) {
    const myElems = [];
    const mySummary = props.data;
    for (const iKey in mySummary) {
        const summary = mySummary[iKey];
        const myElem = _jsx("li", { children: summary }, iKey);
        myElems.push(myElem);
    }
    return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), _jsx("ul", { className: "p-summary", children: myElems })] }));
}
/**
 * ResumeReferences — Render a list of professional references with contact details.
 *
 * @param {any} [props.data] - Array of reference objects (microformat references).
 * @param {any} [props.title] - Section title for references.
 * @param {boolean} [props.collapsible] - When true, the reference list is collapsible.
 */
ResumeReferences.propTypes = {
    /** Reference objects array */
    data: PropTypes.any.isRequired,
    /** Section title */
    title: PropTypes.any.isRequired,
    /** When true, make the section collapsible */
    collapsible: PropTypes.bool,
};
export function ResumeReferences(props) {
    const myElems = [];
    const myReferences = props.data;
    for (const iKey in myReferences) {
        const myReference = myReferences[iKey];
        // const myElem = <li key={iKey}>{myReference[iKey]}</li>;
        const myElem = _jsx(ResumeReference, { data: myReference }, iKey);
        myElems.push(myElem);
    }
    if (props.collapsible && props.collapsible == true) {
        return (_jsx(_Fragment, { children: _jsxs("details", { children: [_jsx("summary", { children: _jsx("h2", { children: props.title }) }), _jsx("div", { children: myElems })] }) }));
    }
    else {
        return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), _jsx("div", { children: myElems })] }));
    }
}
/**
 * ResumeReference — Render a single reference entry (name, organization, contact info).
 *
 * @param {any} [props.data] - Reference object with contact properties used for display.
 */
ResumeReference.propTypes = {
    /** Reference object with contact details */
    data: PropTypes.any.isRequired,
};
export function ResumeReference(props) {
    const myReference = props.data.properties;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [(myReference?.url[0]) ? _jsx("a", { href: myReference?.url[0], target: "_blank", className: "u-url", rel: "noopener noreferrer", children: _jsx("span", { className: "p-name", children: myReference?.name[0] }) }) : _jsx("span", { className: "p-name", children: myReference?.name[0] }), ",", ' ', " ", _jsx("span", { className: "p-locality", children: myReference?.locality[0] }), ",", ' ', " ", _jsx("span", { className: "p-region", children: myReference?.region[0] })] }), _jsxs("div", { children: [_jsx("span", { className: "p-job-title", children: myReference["job-title"][0] }), ",", ' ', " ", _jsx("span", { className: "p-org", children: myReference?.org[0] })] }), _jsxs("div", { children: ["email : ", _jsx("a", { href: `mailto:${myReference?.email[0]}`, target: "_blank", rel: "noopener noreferrer", className: "u-email", children: myReference?.email[0] }), ' ', " || phone : ", _jsx("a", { href: `tel:${myReference?.tel[0]}`, className: "p-tel", children: myReference?.tel[0] })] }), _jsx("br", {}), _jsx("hr", {}), _jsx("br", {})] }));
}
/**
 * ResumeProjects — Render projects associated with work history or portfolios.
 *
 * @param {any} [props.data] - Array of project objects with properties like name, url, photo, and note.
 * @param {any} [props.title] - Section title for the projects block.
 * @param {boolean} [props.collapsible] - When true, projects render inside a collapsible element.
 */
ResumeProjects.propTypes = {
    /** Projects array */
    data: PropTypes.any.isRequired,
    /** Section title */
    title: PropTypes.any.isRequired,
    /** When true, make the section collapsible */
    collapsible: PropTypes.bool,
};
export function ResumeProjects(props) {
    const config = usePixelatedConfig();
    const myElems = [];
    const myEvents = props.data;
    for (const iKey in myEvents) {
        const myEvent = myEvents[iKey];
        const myOrg = _jsx("h3", { children: myEvent.properties.location[0].properties.org[0] }, iKey);
        const myProjects = myEvent.properties.projects;
        const projects = myProjects?.map((project, iKey) => _jsxs("li", { className: "p-project", children: [(project.properties.url[0])
                    ? _jsx("a", { href: project.properties.url[0], target: "_blank", className: "u-url", rel: "noreferrer", children: _jsx("span", { className: "p-name", children: project.properties.name[0] }) })
                    : _jsx("span", { className: "p-name", children: project.properties.name[0] }), ' ', " ", (project.properties.photo[0])
                    ? _jsx("a", { href: project.properties.photo[0], target: "_blank", className: "u-photo", rel: "noreferrer", children: _jsx(SmartImage, { src: 'https://www.pixelated.tech/images/icons/img.png', title: project.properties.name[0], alt: project.properties.name[0], className: 'u-photo-icon', cloudinaryEnv: config?.integrations?.cloudinary?.product_env, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms }) })
                    : null, (project.properties.note[0]) ? _jsx("div", { className: "p=note", children: project.properties.note[0] }) : null] }, "i" + iKey));
        if (myProjects) {
            myElems.push(myOrg);
            myElems.push(_jsx("ul", { children: projects }, "q-" + iKey));
        }
    }
    if (props.collapsible && props.collapsible == true) {
        return (_jsx(_Fragment, { children: _jsxs("details", { children: [_jsx("summary", { children: _jsx("h2", { children: props.title }) }), _jsx("ul", { children: myElems })] }) }));
    }
    else {
        return (_jsxs(_Fragment, { children: [_jsx("h2", { children: props.title }), myElems] }));
    }
}
